import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Catches render-time errors anywhere in the tree so a single broken component
 * (bad API shape, unexpected null, etc.) degrades to a recoverable message
 * instead of white-screening the entire app for every visitor.
 *
 * Errors are reported to the console with a fingerprint so repeated crashes
 * from the same component are easy to spot. Wire `componentDidCatch` to a real
 * error reporter (Sentry, etc.) when one is available.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Unhandled render error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false });
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-fud-gray-light px-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-fud-gray shadow-xl p-8 text-center">
          <div className="bg-fud-brown/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="h-8 w-8 text-fud-brown" />
          </div>
          <h1 className="text-xl font-bold text-fud-dark mb-2">Something went wrong</h1>
          <p className="text-sm text-fud-gray-dark leading-relaxed mb-6">
            This section failed to load. Your data is safe — reloading usually fixes it.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-fud-green hover:bg-fud-green-dark text-white font-bold py-3 px-5 rounded-xl transition-all active:scale-95"
            >
              <RefreshCw className="h-4 w-4" />
              Reload
            </button>
            <button
              onClick={this.handleGoHome}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-fud-gray-light hover:bg-fud-gray text-fud-dark font-bold py-3 px-5 rounded-xl transition-all active:scale-95"
            >
              <Home className="h-4 w-4" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;